import { Formik, FormikHelpers } from "formik";
import { FC, useMemo } from "react";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import yup from "app/util/yup";
import { useReportAccessInformationQuery, useUpdateReportAccessInformationMutation } from "app/api/graph/types";
import { reportAccessInformationGraphId } from "app/api/graph/helpers";
import { RichTextEditor } from "app/component/form/text-editor/RichTextEditor";
import { LoadingSpinner } from "app/component/util/LoadingSpinner";
import { handleError, handleSuccess } from "app/api/graph/form";
import { StatusAlert } from "app/component/form/StatusAlert";

type Props = {
    accessInformationId: number;
    onUpdate?: () => void;
};

type FormValues = {
    additionalInformation: string;
};

const schema: yup.ObjectSchema<FormValues> = yup.object({
    additionalInformation: yup.string().required(),
});

export const EditReportAccessInformationForm: FC<Props> = ({ accessInformationId, onUpdate }) => {
    const { data, loading, error } = useReportAccessInformationQuery({
        variables: { id: reportAccessInformationGraphId(accessInformationId) },
    });
    const accessInformation = data?.reportAccessInformation;
    const [updateAccessInformation] = useUpdateReportAccessInformationMutation({});

    const initialValues: FormValues = useMemo(
        () => ({
            additionalInformation: accessInformation?.additionalInformation ?? "",
        }),
        [accessInformation],
    );

    if (loading) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!accessInformation) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    const onSubmit = (values: FormValues, actions: FormikHelpers<FormValues>) =>
        updateAccessInformation({ variables: { input: { id: accessInformation.id, ...values } } })
            .then(() => onUpdate?.())
            .then(() => handleSuccess(actions))
            .catch((error) => handleError(error, actions))
            .finally(() => actions.setSubmitting(false));

    return (
        <Formik validationSchema={schema} onSubmit={onSubmit} initialValues={initialValues}>
            {({ handleSubmit, setFieldValue, setFieldTouched, values, isSubmitting, errors, touched, status }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <StatusAlert status={status as unknown} />
                    <Form.Group className="mb-3">
                        <Form.Label>Additional information</Form.Label>
                        <RichTextEditor
                            value={values.additionalInformation ?? ""}
                            onChange={(value) => setFieldValue("additionalInformation", value)}
                            onBlur={() => setFieldTouched("additionalInformation")}
                            isInvalid={touched.additionalInformation && !!errors.additionalInformation}
                            singleMode
                        />
                        <Form.Control.Feedback type="invalid">{errors.additionalInformation}</Form.Control.Feedback>
                    </Form.Group>
                    <Button type="submit" disabled={isSubmitting}>
                        Update
                    </Button>
                </Form>
            )}
        </Formik>
    );
};
